import { useCallback, useEffect, useRef, useState } from 'react';
import { AREA_KIND_LABEL, PanelInput, type AreaDto, type AreaKind, type PanelAdminDto, type PanelCheckDto } from '@app/shared';
import { api } from '../../api';
import { platform } from '../../platform';
import { useApp } from '../../state';
import { Icon, LinkStatus, Sheet } from '../../ui';
import { areaPath } from '../Panels';
import { SubHead } from './SubHead';

/** Оборудование: дерево участков и панели (адрес, порт, пароль VNC, режимы). */
export function Equipment({ panelId }: { panelId?: string } = {}) {
  const { fail, open } = useApp();
  const [areas, setAreas] = useState<AreaDto[]>([]);
  const [panels, setPanels] = useState<PanelAdminDto[] | null>(null);
  const [editPanel, setEditPanel] = useState<PanelAdminDto | 'new' | null>(null);
  const [editArea, setEditArea] = useState<AreaDto | 'new' | null>(null);

  const load = useCallback(async () => {
    try {
      const [a, p] = await Promise.all([api.areas(), api.adminPanels()]);
      setAreas(a);
      setPanels(p);
    } catch (e) {
      fail(e);
    }
  }, [fail]);
  useEffect(() => void load(), [load]);
  // Пришли из списка панелей или с экрана панели — сразу открываем её настройки.
  const opened = useRef(false);
  useEffect(() => {
    if (!panelId || opened.current || !panels) return;
    opened.current = true;
    const p = panels.find((x) => x.id === panelId);
    if (p) setEditPanel(p);
  }, [panelId, panels]);

  const sortedAreas = [...areas].sort((a, b) => areaPath(areas, a.id).localeCompare(areaPath(areas, b.id), 'ru'));
  const sortedPanels = [...(panels ?? [])].sort((a, b) => `${areaPath(areas, a.areaId)} ${a.name}`.localeCompare(`${areaPath(areas, b.areaId)} ${b.name}`, 'ru'));

  return (
    <div className="stack">
      <SubHead title="Оборудование" />

      <div className="card flat small">
        Адрес панели — IP основного ПК в сети завода (сервер видит её через WireGuard), порт — проброшенный порт конкретной машины. Пароль VNC хранится на сервере в
        зашифрованном виде и на телефоны не передаётся.
      </div>

      <div className="row">
        <h2 className="section-title grow">Панели · {panels?.length ?? 0}</h2>
        <button type="button" className="btn btn-primary btn-sm" onClick={() => setEditPanel('new')}>
          <Icon name="plus" size={16} />
          Панель
        </button>
      </div>
      {!panels && <div className="skeleton" />}
      {panels && panels.length > 0 && (
        <div className="list">
          {sortedPanels.map((p) => (
            <button key={p.id} type="button" className="item" onClick={() => setEditPanel(p)}>
              <span className="grow stack" style={{ gap: 2 }}>
                <span className="title ellipsis">
                  {p.name}
                  {!p.enabled && <span className="muted"> · выключена</span>}
                </span>
                <span className="sub ellipsis">
                  {p.host}:{p.port} · {areaPath(areas, p.areaId)}
                </span>
                <span className="row wrap" style={{ gap: 8 }}>
                  <LinkStatus link={p.link} />
                  {!p.controlAllowed && <span className="badge outline">только просмотр</span>}
                  {!p.hasPassword && <span className="badge outline">без пароля</span>}
                </span>
              </span>
              <span className="chev">
                <Icon name="edit" size={18} />
              </span>
            </button>
          ))}
        </div>
      )}

      <div className="row" style={{ marginTop: 8 }}>
        <h2 className="section-title grow">Участки · {areas.length}</h2>
        <button type="button" className="btn btn-ghost btn-sm" onClick={() => setEditArea('new')}>
          <Icon name="plus" size={16} />
          Участок
        </button>
      </div>
      {areas.length === 0 ? (
        <p className="small muted" style={{ margin: 0 }}>
          Участки группируют панели и определяют, кто что видит: оператору открывают его участок, начальнику — всё. Например: «Цех 1» → «Линия сборки».
        </p>
      ) : (
        <div className="list">
          {sortedAreas.map((a) => (
            <button key={a.id} type="button" className="item" style={{ minHeight: 52 }} onClick={() => setEditArea(a)}>
              <span className="grow">
                <span className="title" style={{ fontSize: 14 }}>
                  {areaPath(areas, a.id)}
                </span>
                <span className="sub" style={{ display: 'block' }}>
                  {AREA_KIND_LABEL[a.kind]} · {(panels ?? []).filter((p) => p.areaId === a.id).length} пан.
                </span>
              </span>
              <span className="chev">
                <Icon name="edit" size={18} />
              </span>
            </button>
          ))}
        </div>
      )}

      <button type="button" className="btn btn-ghost" onClick={() => open({ name: 'sessions' })}>
        <Icon name="log" size={18} />
        Журнал подключений
      </button>

      {editPanel && (
        <PanelSheet
          panel={editPanel === 'new' ? null : editPanel}
          areas={sortedAreas}
          allAreas={areas}
          template={panels?.[panels.length - 1] ?? null}
          onClose={() => setEditPanel(null)}
          onSaved={async () => {
            setEditPanel(null);
            await load();
          }}
        />
      )}
      {editArea && (
        <AreaSheet
          area={editArea === 'new' ? null : editArea}
          areas={sortedAreas}
          allAreas={areas}
          onClose={() => setEditArea(null)}
          onSaved={async () => {
            setEditArea(null);
            await load();
          }}
        />
      )}
    </div>
  );
}

function PanelSheet({
  panel,
  areas,
  allAreas,
  template,
  onClose,
  onSaved,
}: {
  panel: PanelAdminDto | null;
  areas: AreaDto[];
  allAreas: AreaDto[];
  template: PanelAdminDto | null;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const { fail, toast } = useApp();
  // Новая панель обычно на том же ПК, что и предыдущая, со следующим портом.
  const [name, setName] = useState(panel?.name ?? '');
  const [description, setDescription] = useState(panel?.description ?? '');
  const [areaId, setAreaId] = useState<string>(panel?.areaId ?? template?.areaId ?? '');
  const [host, setHost] = useState(panel?.host ?? template?.host ?? '');
  const [port, setPort] = useState(String(panel?.port ?? (template ? template.port + 1 : 5900)));
  const [password, setPassword] = useState('');
  const [passwordMode, setPasswordMode] = useState<'keep' | 'set' | 'clear'>(panel?.hasPassword ? 'keep' : 'set');
  const [controlAllowed, setControlAllowed] = useState(panel?.controlAllowed ?? true);
  const [enabled, setEnabled] = useState(panel?.enabled ?? true);
  const [check, setCheck] = useState<PanelCheckDto | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const pwValue = (): string | null | undefined => (passwordMode === 'keep' ? undefined : passwordMode === 'clear' ? null : password || null);

  function input() {
    return PanelInput.safeParse({
      name,
      description: description || null,
      areaId: areaId || null,
      host: host.trim(),
      port: Number(port),
      password: pwValue(),
      controlAllowed,
      enabled,
    });
  }

  async function runCheck() {
    const p = PanelInput.pick({ host: true, port: true }).safeParse({ host: host.trim(), port: Number(port) });
    if (!p.success) return setError(p.error.issues[0]?.message ?? 'Проверьте адрес');
    setError(null);
    setBusy(true);
    setCheck(null);
    try {
      // Пароль из формы, если его меняют; иначе сервер возьмёт сохранённый.
      const r = await api.checkPanel(panel?.id ?? null, { ...p.data, ...(pwValue() !== undefined ? { password: pwValue() } : {}) });
      setCheck(r);
      platform.haptic(r.ok ? 'success' : 'warning');
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  }

  async function save() {
    const p = input();
    if (!p.success) return setError(p.error.issues[0]?.message ?? 'Проверьте поля');
    setError(null);
    setBusy(true);
    try {
      if (panel) await api.updatePanel(panel.id, p.data);
      else await api.createPanel(p.data);
      platform.haptic('success');
      toast(panel ? 'Панель сохранена' : 'Панель добавлена');
      await onSaved();
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!panel || !(await platform.confirm(`Удалить панель «${panel.name}»? Журнал её подключений тоже удалится.`))) return;
    setBusy(true);
    try {
      await api.deletePanel(panel.id);
      toast('Панель удалена');
      await onSaved();
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Sheet title={panel ? panel.name : 'Новая панель'} onClose={onClose}>
      <div className="field">
        <label htmlFor="pn">Название</label>
        <input id="pn" className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Пресс ПК-160, линия 2" />
      </div>
      <div className="field">
        <label htmlFor="pa">Участок</label>
        <select id="pa" className="input" value={areaId} onChange={(e) => setAreaId(e.target.value)}>
          <option value="">— без участка (видят только роли «все участки») —</option>
          {areas.map((a) => (
            <option key={a.id} value={a.id}>
              {areaPath(allAreas, a.id)}
            </option>
          ))}
        </select>
      </div>
      <div className="grid2">
        <div className="field">
          <label htmlFor="ph">Адрес ПК</label>
          <input id="ph" className="input" value={host} inputMode="url" autoCapitalize="off" autoCorrect="off" spellCheck={false} onChange={(e) => setHost(e.target.value.replace(/,/g, ".").replace(/\s/g, ""))} placeholder="192.168.1.20" />
        </div>
        <div className="field">
          <label htmlFor="pp">Порт</label>
          <input id="pp" className="input" value={port} inputMode="numeric" onChange={(e) => setPort(e.target.value.replace(/\D/g, '').slice(0, 5))} />
        </div>
      </div>
      <div className="field">
        <span className="label">Пароль VNC</span>
        {panel?.hasPassword && (
          <div className="seg" role="group" aria-label="Пароль">
            <button type="button" aria-pressed={passwordMode === 'keep'} onClick={() => setPasswordMode('keep')}>
              Оставить
            </button>
            <button type="button" aria-pressed={passwordMode === 'set'} onClick={() => setPasswordMode('set')}>
              Заменить
            </button>
            <button type="button" aria-pressed={passwordMode === 'clear'} onClick={() => setPasswordMode('clear')}>
              Убрать
            </button>
          </div>
        )}
        {passwordMode === 'set' && (
          <input className="input" type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="если на панели задан пароль" />
        )}
        <span className="hint">VNC учитывает только первые 8 символов пароля.</span>
      </div>
      <div className="field">
        <label htmlFor="pd">Примечание</label>
        <input id="pd" className="input" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Что на экране, на что смотреть" />
      </div>
      <label className="check">
        <input type="checkbox" checked={controlAllowed} onChange={(e) => setControlAllowed(e.target.checked)} />
        <span>
          Разрешить управление
          <span className="hint" style={{ display: 'block' }}>
            Выключите для панелей, где нажатия с телефона недопустимы — останется только просмотр для всех.
          </span>
        </span>
      </label>
      <label className="check">
        <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
        <span>Панель включена (видна в списке)</span>
      </label>

      <button type="button" className="btn btn-outline btn-block" disabled={busy} onClick={() => void runCheck()}>
        <Icon name="pulse" size={18} />
        Проверить связь и пароль
      </button>
      {check && (
        <div className={`result ${check.ok ? 'ok' : 'bad'}`} role="status">
          {check.ok ? '✓ ' : ''}
          {check.message}
          {check.desktopName ? ` · «${check.desktopName}»` : ''}
        </div>
      )}
      {error && <p className="error">{error}</p>}

      <button type="button" className="btn btn-primary btn-block" disabled={busy} onClick={() => void save()}>
        {panel ? 'Сохранить' : 'Добавить панель'}
      </button>
      {panel && (
        <button type="button" className="btn btn-danger btn-block" disabled={busy} onClick={() => void remove()}>
          Удалить панель
        </button>
      )}
    </Sheet>
  );
}

function AreaSheet({ area, areas, allAreas, onClose, onSaved }: { area: AreaDto | null; areas: AreaDto[]; allAreas: AreaDto[]; onClose: () => void; onSaved: () => Promise<void> }) {
  const { fail, toast } = useApp();
  const [name, setName] = useState(area?.name ?? '');
  const [kind, setKind] = useState<AreaKind>(area?.kind ?? 'line');
  const [parentId, setParentId] = useState(area?.parentId ?? '');
  const [busy, setBusy] = useState(false);

  async function save() {
    if (!name.trim()) return toast('Введите название');
    setBusy(true);
    try {
      const body = { name: name.trim(), kind, parentId: parentId || null };
      if (area) await api.updateArea(area.id, body);
      else await api.createArea(body);
      await onSaved();
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!area || !(await platform.confirm(`Удалить участок «${area.name}»?`))) return;
    setBusy(true);
    try {
      await api.deleteArea(area.id);
      await onSaved();
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Sheet title={area ? area.name : 'Новый участок'} onClose={onClose}>
      <div className="field">
        <label htmlFor="an">Название</label>
        <input id="an" className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Линия сборки" />
      </div>
      <div className="field">
        <span className="label">Тип</span>
        <div className="chips">
          {(Object.keys(AREA_KIND_LABEL) as AreaKind[]).map((k) => (
            <button key={k} type="button" className="chip" aria-pressed={kind === k} onClick={() => setKind(k)}>
              {AREA_KIND_LABEL[k]}
            </button>
          ))}
        </div>
      </div>
      <div className="field">
        <label htmlFor="ap">Входит в</label>
        <select id="ap" className="input" value={parentId} onChange={(e) => setParentId(e.target.value)}>
          <option value="">— верхний уровень —</option>
          {areas
            .filter((a) => a.id !== area?.id)
            .map((a) => (
              <option key={a.id} value={a.id}>
                {areaPath(allAreas, a.id)}
              </option>
            ))}
        </select>
      </div>
      <button type="button" className="btn btn-primary btn-block" disabled={busy} onClick={() => void save()}>
        {area ? 'Сохранить' : 'Добавить участок'}
      </button>
      {area && (
        <button type="button" className="btn btn-danger btn-block" disabled={busy} onClick={() => void remove()}>
          Удалить участок
        </button>
      )}
    </Sheet>
  );
}
